import { templates } from './templates';
import { FilterTypes } from './templates/types';

/**
 * Onboarding details structure from the questionnaire
 */
export interface OnboardingDetails {
  role_block?: string;
  website_block?: string;
  help_with_block?: string;
  use_purpose_block?: string;
}

/**
 * Maps role options to relevant template tags
 */
const roleToTags: Record<string, FilterTypes[]> = {
  customer_success_option: ['customer-success', 'get-feedback'],
  marketing_option: ['marketing', 'lead-generation', 'engage-audience'],
  hr_option: ['human-resources', 'recruit-talent'],
  it_dev_option: ['product', 'get-feedback'],
  product_research_option: ['product', 'get-feedback', 'marketing'],
  sales_option: ['sales', 'make-sales', 'lead-generation'],
  founder_ceo_option: ['lead-generation', 'sales', 'marketing', 'get-feedback'],
};

/**
 * Maps "help with" options to relevant template tags
 */
const helpWithToTags: Record<string, FilterTypes[]> = {
  get_feedback_option: ['get-feedback', 'product', 'customer-success'],
  recruit_talent_option: ['recruit-talent', 'human-resources'],
  manage_events_option: ['plan-events', 'engage-audience'],
  more_customers_option: ['make-sales', 'sales', 'lead-generation'],
  engage_audience_option: ['engage-audience', 'marketing'],
  conduct_research_option: ['get-feedback', 'product', 'marketing'],
};

/**
 * Maps use purpose options to relevant template tags
 */
const usePurposeToTags: Record<string, FilterTypes[]> = {
  school_option: ['get-feedback', 'engage-audience'],
  personal_option: ['engage-audience', 'get-feedback'],
  work_option: ['lead-generation', 'get-feedback', 'product'],
};

/**
 * Gets recommended template IDs based on onboarding details
 * @param onboardingDetails - The user's onboarding questionnaire answers
 * @param limit - Maximum number of templates to recommend (default: 5)
 * @returns Array of template IDs sorted by relevance
 */
export function getRecommendedTemplateIds(
  onboardingDetails: OnboardingDetails | null | undefined,
  limit: number = 5
): string[] {
  if (!onboardingDetails) {
    // Return popular templates as fallback
    return templates
      .filter(t => t.popular)
      .slice(0, limit)
      .map(t => t.id);
  }

  // Collect all relevant tags based on answers
  const relevantTags: FilterTypes[] = [];

  // Add tags from role
  if (onboardingDetails.role_block && roleToTags[onboardingDetails.role_block]) {
    relevantTags.push(...roleToTags[onboardingDetails.role_block]);
  }

  // Add tags from "help with" (highest priority)
  if (onboardingDetails.help_with_block && helpWithToTags[onboardingDetails.help_with_block]) {
    // Add these tags twice to give them more weight
    relevantTags.push(...helpWithToTags[onboardingDetails.help_with_block]);
    relevantTags.push(...helpWithToTags[onboardingDetails.help_with_block]);
  }

  // Add tags from use purpose
  if (onboardingDetails.use_purpose_block && usePurposeToTags[onboardingDetails.use_purpose_block]) {
    relevantTags.push(...usePurposeToTags[onboardingDetails.use_purpose_block]);
  }

  // If no relevant tags found, return popular templates
  if (relevantTags.length === 0) {
    return templates
      .filter(t => t.popular)
      .slice(0, limit)
      .map(t => t.id);
  }

  // Score each template based on matching tags
  const templateScores = templates.map(template => {
    let score = 0;

    // Count how many relevant tags this template has
    for (const tag of relevantTags) {
      if (template.tags.includes(tag)) {
        score += 1;
      }
    }

    return { template, score, isPopular: !!template.popular };
  });

  // Filter to only templates with matching tags, then sort:
  // 1. Popular templates first
  // 2. Then by score (descending)
  return templateScores
    .filter(({ score }) => score > 0)
    .sort((a, b) => {
      // Popular templates come first
      if (a.isPopular && !b.isPopular) return -1;
      if (!a.isPopular && b.isPopular) return 1;
      // Within same popularity tier, sort by score
      return b.score - a.score;
    })
    .slice(0, limit)
    .map(({ template }) => template.id);
}
